import { useState } from 'react'
import { TimelineHeader, TimelineSidebar } from '@/pages/timeline/components'
import { Outlet, useLocation } from 'react-router-dom'
import { FilterBreadcrumb } from '@/components/FilterBreadcrumb'
import { useAuth } from '@/contexts/auth'
import { DateRange } from 'react-day-picker'
import { TimelineFilters } from '@/types/filter'

export default function TimelinePage() {
  const location = useLocation()
  const { user } = useAuth()
  const today = new Date()
  const initialDateRange: DateRange = { from: today, to: today }
  const initialStartDate = today.toISOString().split('T')[0]
  const initialEndDate = today.toISOString().split('T')[0]
  
  const [filters, setFilters] = useState<TimelineFilters>({
    search: '',
    territories: '',
    contexts: '',
    topics: '',
    partners: '',
    sources: '',
    favorited: undefined,
    created_by: undefined,
    date_range: 'MONTH',
    start_date: initialStartDate,
    end_date: initialEndDate
  })
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false)
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | undefined>(initialDateRange)
  
  const handleDateChange = (range: DateRange | undefined) => {
    setSelectedDateRange(range)
    if (range?.from && range?.to) {
      const startDate = range.from.toISOString().split('T')[0]
      const endDate = range.to.toISOString().split('T')[0]
      setFilters((prev) => ({ 
        ...prev, 
        start_date: startDate,
        end_date: endDate,
        date_range: 'MONTH'
      }))
    } else if (range?.from) {
      const startDate = range.from.toISOString().split('T')[0]
      setFilters((prev) => ({ 
        ...prev, 
        start_date: startDate,
        end_date: startDate,
        date_range: 'MONTH'
      }))
    } else {
      setFilters((prev) => {
        const newFilters = { ...prev }
        delete newFilters.start_date
        delete newFilters.end_date
        delete newFilters.date_range
        return newFilters
      })
    }
  }
  
  const getActiveTab = () => {
    if (location.pathname.includes('/curated')) return 'curated'
    if (location.pathname.includes('/rejected')) return 'rejected'
    if (location.pathname.includes('/raw')) return 'raw'
    const isRootUser = user?.email === 'root@live.com'
    return isRootUser ? 'raw' : 'curated'
  }

  const activeTab = getActiveTab()

  const handleFiltersChange = (newFilters: TimelineFilters) => {
    setFilters(newFilters)
  }

  const handleSearch = (searchValue: string) => {
    setFilters((prev: TimelineFilters) => ({ ...prev, search: searchValue }))
  }

  const handleRemoveFilter = (type: string, value?: string) => {
    if (type === 'territories' && value) {
      const currentIds = filters.territories?.split(';').filter(Boolean) || []
      const newIds = currentIds.filter(id => id !== value)
      setFilters((prev) => ({ ...prev, territories: newIds.join(';') || '' }))
    } else if (type === 'contexts' && value) {
      const currentIds = filters.contexts?.split(';').filter(Boolean) || []
      const newIds = currentIds.filter(id => id !== value)
      setFilters((prev) => ({ ...prev, contexts: newIds.join(';') || '' }))
    } else if (type === 'topics' && value) {
      const currentIds = filters.topics?.split(';').filter(Boolean) || []
      const newIds = currentIds.filter(id => id !== value)
      setFilters((prev) => ({ ...prev, topics: newIds.join(';') || '' }))
    } else if (type === 'partners' && value) {
      const currentIds = filters.partners?.split(';').filter(Boolean) || []
      const newIds = currentIds.filter(id => id !== value)
      setFilters((prev) => ({ ...prev, partners: newIds.join(';') || '' }))
    } else if (type === 'sources' && value) {
      const currentIds = filters.sources?.split(';').filter(Boolean) || []
      const newIds = currentIds.filter(id => id !== value)
      setFilters((prev) => ({ ...prev, sources: newIds.join(';') || '' }))
    } else if (type === 'favorited') {
      setFilters((prev) => ({ ...prev, favorited: undefined }))
    } else if (type === 'created_by') {
      setFilters((prev) => ({ ...prev, created_by: undefined }))
    }
  }

  return (
    <div className="h-full container mx-auto max-w-7xl flex flex-col">
      <div className="p-6">
        <TimelineHeader 
          activeTab={activeTab}
          onSearch={handleSearch}
          searchValue={filters.search}
          selectedDate={selectedDateRange}
          onDateChange={handleDateChange}
        />
        <div className="mt-4">
          <FilterBreadcrumb
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
          />
        </div>
      </div>
      <div className="flex flex-1">
        <div className="flex-1 px-6 overflow-y-auto">
          <Outlet context={{ filters }} />
        </div>
        <TimelineSidebar 
          onFiltersChange={handleFiltersChange}
          currentFilters={filters}
          isFiltersPanelOpen={isFiltersPanelOpen}
          onFiltersPanelChange={setIsFiltersPanelOpen}
        />
      </div>
    </div>
  )
}
